import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  trackEvidenceCategoryClickedSpec,
  trackEvidenceChatAbortedSpec,
  trackEvidenceChatCompletedSpec,
  trackEvidenceChatCtaClickedSpec,
  trackEvidenceChatErrorSpec,
  trackEvidenceChatSubmittedSpec,
  trackEvidenceClickedSpec,
  trackEvidenceContactSalesClickedSpec,
  trackEvidenceCopiedSpec,
  trackEvidenceCopyConfirmationSpec,
  trackEvidenceExperienceFeedbackDismissedSpec,
  trackEvidenceExperienceFeedbackRatingSubmittedSpec,
  trackEvidenceExperienceFeedbackSnoozedSpec,
  trackEvidenceExperienceFeedbackSubmittedSpec,
  trackEvidenceExperienceFeedbackViewedSpec,
  trackEvidenceJurisdictionChangedSpec,
  trackEvidenceLanguageChangedSpec,
  trackEvidenceLiveSuggestionsToggledSpec,
  trackEvidenceNhsContactSalesSubmittedSpec,
  trackEvidenceOnboardingDismissedSpec,
  trackEvidenceOnboardingSavedSpec,
  trackEvidenceOnboardingSkippedSpec,
  trackEvidenceOnboardingViewedSpec,
  trackEvidenceReferenceFeedbackSubmittedSpec,
  trackEvidenceReferenceScrollClickedSpec,
  trackEvidenceReferencesSectionToggledSpec,
  trackEvidenceRequestAccessClickedSpec,
  trackEvidenceResponseFeedbackSubmittedSpec,
  trackEvidenceShareLinkCopiedSpec,
  trackEvidenceShareModalOpenedSpec,
  trackEvidenceShareSocialClickedSpec,
  trackEvidenceSharedChatOpenedSpec,
  trackEvidenceSignupWallViewedSpec,
  trackEvidenceSourceClickedSpec,
  trackEvidenceSourceControlToggledSpec,
  trackEvidenceSpecialtyChangedSpec,
  trackEvidenceSuggestionClickedSpec,
  trackEvidenceToastShownSpec,
  trackEvidenceVerbClickedSpec,
  type EvidenceCategoryClicked,
  type EvidenceChatAborted,
  type EvidenceChatCompleted,
  type EvidenceChatCtaClicked,
  type EvidenceChatError,
  type EvidenceChatSubmitted,
  type EvidenceClicked,
  type EvidenceContactSalesClicked,
  type EvidenceCopied,
  type EvidenceCopyConfirmation,
  type EvidenceExperienceFeedbackDismissed,
  type EvidenceExperienceFeedbackRatingSubmitted,
  type EvidenceExperienceFeedbackSnoozed,
  type EvidenceExperienceFeedbackSubmitted,
  type EvidenceExperienceFeedbackViewed,
  type EvidenceJurisdictionChanged,
  type EvidenceLanguageChanged,
  type EvidenceLiveSuggestionsToggled,
  type EvidenceNhsContactSalesSubmitted,
  type EvidenceOnboardingDismissed,
  type EvidenceOnboardingSaved,
  type EvidenceOnboardingSkipped,
  type EvidenceOnboardingViewed,
  type EvidenceReferenceFeedbackSubmitted,
  type EvidenceReferenceScrollClicked,
  type EvidenceReferencesSectionToggled,
  type EvidenceRequestAccessClicked,
  type EvidenceResponseFeedbackSubmitted,
  type EvidenceShareLinkCopied,
  type EvidenceShareModalOpened,
  type EvidenceShareSocialClicked,
  type EvidenceSharedChatOpened,
  type EvidenceSignupWallViewed,
  type EvidenceSourceClicked,
  type EvidenceSourceControlToggled,
  type EvidenceSpecialtyChanged,
  type EvidenceSuggestionClicked,
  type EvidenceToastShown,
  type EvidenceVerbClicked,
} from '@snowtype/snowplow';
import type { SnowplowEventMap } from '../trackSnowplowEvent';
import {
  trackEvidenceCheckOtherSourcesClickedSpec,
  trackEvidencePromptsBarDismissedSpec,
  trackEvidencePromptsBarExpandedSpec,
  trackEvidencePromptsBarShownSpec,
  trackEvidenceShortcutRunSpec,
  trackEvidenceShortcutSaveToggledSpec,
  trackEvidenceShortcutSearchUsedSpec,
  trackEvidenceShortcutsOpenedSpec,
  trackEvidenceSourcesOpenedSpec,
  trackEvidenceSummarySuggestionDismissedSpec,
  trackEvidenceSummarySuggestionGeneratedSpec,
  trackEvidenceSummarySuggestionShownSpec,
} from './evidence-pending-specs';

/**
 * Amplitude-only field on in-session click events. The published Iglu schemas
 * (`evidence_verb_clicked` / `evidence_suggestion_clicked` 1-0-1) do not
 * include it; stripping here keeps the warehouse ingest from seeing an
 * unknown property without an ADDITION bump.
 */
const omitExperimentArm = (props: unknown): unknown => {
  if (props === null || typeof props !== 'object' || Array.isArray(props)) {
    return props;
  }
  const rest = { ...(props as Record<string, unknown>) };
  delete rest.experiment_arm;
  return rest;
};

export const evidenceEvents: SnowplowEventMap = {
  [TrackingEventName.EvidenceClicked]: (props) =>
    trackEvidenceClickedSpec(props as EvidenceClicked),
  [TrackingEventName.EvidenceCategoryClicked]: (props) =>
    trackEvidenceCategoryClickedSpec(props as EvidenceCategoryClicked),
  [TrackingEventName.EvidenceVerbClicked]: (props) =>
    trackEvidenceVerbClickedSpec(omitExperimentArm(props) as EvidenceVerbClicked),
  [TrackingEventName.EvidenceSuggestionClicked]: (props) =>
    trackEvidenceSuggestionClickedSpec(
      omitExperimentArm(props) as EvidenceSuggestionClicked
    ),
  [TrackingEventName.EvidenceSignUpWallViewed]: (props) =>
    trackEvidenceSignupWallViewedSpec(props as EvidenceSignupWallViewed),
  [TrackingEventName.EvidenceSourceClicked]: (props) =>
    trackEvidenceSourceClickedSpec(props as EvidenceSourceClicked),
  [TrackingEventName.EvidenceRequestAccessClicked]: (props) =>
    trackEvidenceRequestAccessClickedSpec(props as EvidenceRequestAccessClicked),
  [TrackingEventName.EvidenceContactSalesClicked]: (props) =>
    trackEvidenceContactSalesClickedSpec(props as EvidenceContactSalesClicked),
  [TrackingEventName.EvidenceNhsContactSalesSubmitted]: (props) =>
    trackEvidenceNhsContactSalesSubmittedSpec(props as EvidenceNhsContactSalesSubmitted),
  [TrackingEventName.EvidenceResponseFeedbackSubmitted]: (props) =>
    trackEvidenceResponseFeedbackSubmittedSpec(
      props as EvidenceResponseFeedbackSubmitted
    ),
  [TrackingEventName.EvidenceReferenceFeedbackSubmitted]: (props) =>
    trackEvidenceReferenceFeedbackSubmittedSpec(
      props as EvidenceReferenceFeedbackSubmitted
    ),
  [TrackingEventName.EvidenceReferenceScrollClicked]: (props) =>
    trackEvidenceReferenceScrollClickedSpec(props as EvidenceReferenceScrollClicked),
  [TrackingEventName.EvidenceReferencesSectionToggled]: (props) =>
    trackEvidenceReferencesSectionToggledSpec(props as EvidenceReferencesSectionToggled),
  [TrackingEventName.EvidenceChatSubmitted]: (props) =>
    trackEvidenceChatSubmittedSpec(props as EvidenceChatSubmitted),
  [TrackingEventName.EvidenceChatCompleted]: (props) =>
    trackEvidenceChatCompletedSpec(props as EvidenceChatCompleted),
  [TrackingEventName.EvidenceChatError]: (props) =>
    trackEvidenceChatErrorSpec(props as EvidenceChatError),
  [TrackingEventName.EvidenceChatAborted]: (props) =>
    trackEvidenceChatAbortedSpec(props as EvidenceChatAborted),
  [TrackingEventName.EvidenceChatCtaClicked]: (props) =>
    trackEvidenceChatCtaClickedSpec(props as EvidenceChatCtaClicked),
  [TrackingEventName.EvidenceCopyConfirmation]: (props) =>
    trackEvidenceCopyConfirmationSpec(props as EvidenceCopyConfirmation),
  [TrackingEventName.EvidenceCopied]: (props) =>
    trackEvidenceCopiedSpec(props as EvidenceCopied),
  [TrackingEventName.EvidenceCheckOtherSourcesClicked]: (props) =>
    trackEvidenceCheckOtherSourcesClickedSpec(props as Record<string, unknown>),
  [TrackingEventName.EvidencePromptsBarShown]: (props) =>
    trackEvidencePromptsBarShownSpec(props as Record<string, unknown>),
  [TrackingEventName.EvidencePromptsBarDismissed]: (props) =>
    trackEvidencePromptsBarDismissedSpec(props as Record<string, unknown>),
  [TrackingEventName.EvidencePromptsBarExpanded]: (props) =>
    trackEvidencePromptsBarExpandedSpec(props as Record<string, unknown>),
  [TrackingEventName.EvidenceShortcutsOpened]: (props) =>
    trackEvidenceShortcutsOpenedSpec(props as Record<string, unknown>),
  [TrackingEventName.EvidenceShortcutRun]: (props) =>
    trackEvidenceShortcutRunSpec(props as Record<string, unknown>),
  [TrackingEventName.EvidenceShortcutSaveToggled]: (props) =>
    trackEvidenceShortcutSaveToggledSpec(props as Record<string, unknown>),
  [TrackingEventName.EvidenceShortcutSearchUsed]: (props) =>
    trackEvidenceShortcutSearchUsedSpec(props as Record<string, unknown>),
  [TrackingEventName.EvidenceSourcesOpened]: (props) =>
    trackEvidenceSourcesOpenedSpec(props as Record<string, unknown>),
  [TrackingEventName.EvidenceSummarySuggestionShown]: (props) =>
    trackEvidenceSummarySuggestionShownSpec(props as Record<string, unknown>),
  [TrackingEventName.EvidenceSummarySuggestionDismissed]: (props) =>
    trackEvidenceSummarySuggestionDismissedSpec(props as Record<string, unknown>),
  [TrackingEventName.EvidenceSummarySuggestionGenerated]: (props) =>
    trackEvidenceSummarySuggestionGeneratedSpec(props as Record<string, unknown>),
  [TrackingEventName.EvidenceOnboardingViewed]: (props) =>
    trackEvidenceOnboardingViewedSpec(props as EvidenceOnboardingViewed),
  [TrackingEventName.EvidenceOnboardingSaved]: (props) =>
    trackEvidenceOnboardingSavedSpec(props as EvidenceOnboardingSaved),
  [TrackingEventName.EvidenceOnboardingSkipped]: (props) =>
    trackEvidenceOnboardingSkippedSpec(props as EvidenceOnboardingSkipped),
  [TrackingEventName.EvidenceOnboardingDismissed]: (props) =>
    trackEvidenceOnboardingDismissedSpec(props as EvidenceOnboardingDismissed),
  [TrackingEventName.EvidenceLanguageChanged]: (props) =>
    trackEvidenceLanguageChangedSpec(props as EvidenceLanguageChanged),
  [TrackingEventName.EvidenceSpecialtyChanged]: (props) =>
    trackEvidenceSpecialtyChangedSpec(props as EvidenceSpecialtyChanged),
  [TrackingEventName.EvidenceJurisdictionChanged]: (props) =>
    trackEvidenceJurisdictionChangedSpec(props as EvidenceJurisdictionChanged),
  [TrackingEventName.EvidenceToastShown]: (props) =>
    trackEvidenceToastShownSpec(props as EvidenceToastShown),
  [TrackingEventName.EvidenceSourceControlToggled]: (props) =>
    trackEvidenceSourceControlToggledSpec(props as EvidenceSourceControlToggled),
  [TrackingEventName.EvidenceLiveSuggestionsToggled]: (props) =>
    trackEvidenceLiveSuggestionsToggledSpec(props as EvidenceLiveSuggestionsToggled),
  [TrackingEventName.EvidenceExperienceFeedbackViewed]: (props) =>
    trackEvidenceExperienceFeedbackViewedSpec(props as EvidenceExperienceFeedbackViewed),
  [TrackingEventName.EvidenceExperienceFeedbackRatingSubmitted]: (props) =>
    trackEvidenceExperienceFeedbackRatingSubmittedSpec(
      props as EvidenceExperienceFeedbackRatingSubmitted
    ),
  [TrackingEventName.EvidenceExperienceFeedbackSubmitted]: (props) =>
    trackEvidenceExperienceFeedbackSubmittedSpec(
      props as EvidenceExperienceFeedbackSubmitted
    ),
  [TrackingEventName.EvidenceExperienceFeedbackSnoozed]: (props) =>
    trackEvidenceExperienceFeedbackSnoozedSpec(
      props as EvidenceExperienceFeedbackSnoozed
    ),
  [TrackingEventName.EvidenceExperienceFeedbackDismissed]: (props) =>
    trackEvidenceExperienceFeedbackDismissedSpec(
      props as EvidenceExperienceFeedbackDismissed
    ),
  [TrackingEventName.EvidenceShareLinkCopied]: (props) =>
    trackEvidenceShareLinkCopiedSpec(props as EvidenceShareLinkCopied),
  [TrackingEventName.EvidenceShareModalOpened]: (props) =>
    trackEvidenceShareModalOpenedSpec(props as EvidenceShareModalOpened),
  [TrackingEventName.EvidenceShareSocialClicked]: (props) =>
    trackEvidenceShareSocialClickedSpec(props as EvidenceShareSocialClicked),
  [TrackingEventName.EvidenceSharedChatOpened]: (props) =>
    trackEvidenceSharedChatOpenedSpec(props as EvidenceSharedChatOpened),
};
